/**
 * MaintenanceBanner — system-wide broadcast banner.
 *
 * Polls `/api/v1/system/maintenance` every 15s. When the backend
 * reports `active=true`, renders a fixed-top warning bar over the
 * entire app. When `active=false`, the bar is unrendered.
 *
 * Triggered by:
 *   - Platform admin manually via /platform/configuration
 *   - The deploy workflow (.github/workflows/deploy.yml) hitting
 *     /api/v1/platform/system/maintenance/start at job-start and
 *     /api/v1/platform/system/maintenance/end on completion.
 *
 * Public — the GET endpoint requires no auth so the banner shows
 * even on the landing page / login screen.
 */

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

interface MaintenanceState {
  active: boolean;
  message: string;
  started_at: string | null;
  /** CI is shipping a commit. NOT an outage, and deliberately a separate field
   *  from `active` — see the note on the deploy strip below. */
  deploying?: boolean;
  deploy_started_at?: string | null;
}

const POLL_MS = 15_000;

async function fetchMaintenanceState(): Promise<MaintenanceState | null> {
  try {
    const res = await fetch("/api/v1/system/maintenance", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    return (await res.json()) as MaintenanceState;
  } catch {
    return null;
  }
}

/** Whether a deploy is running, for callers that must not speak over the deploy
 *  strip. Exported from here rather than polled again: this component already
 *  asks every 15s, and a second poller for the same boolean is a cost with no
 *  benefit — and two pollers can disagree, which is how two notices end up on
 *  screen at once. */
let _deploying = false;
export function isDeploying(): boolean {
  return _deploying;
}

export function MaintenanceBanner() {
  const [state, setState] = useState<MaintenanceState | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await fetchMaintenanceState();
      _deploying = Boolean(next?.deploying);
      if (!cancelled) setState(next);
    };
    // Poll immediately, then every POLL_MS.
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // A DEPLOY IS NOT AN OUTAGE.
  //
  // Founder, 2026-09-03: "I need the mantenance banner to be removed when we are
  // deploying and we need o find a nonintrusive way to make people aware that a
  // deploy is occuring without obstructing the users ability to interact with any
  // aspects of the applicaion."
  //
  // Deploys happen constantly — 19 times on the day this was written — and the
  // amber bar below treats each one as an outage. Doing that to a routine event
  // teaches people to dismiss the bar, which is the one thing it cannot afford,
  // because deploy.yml DELIBERATELY leaves it on when a deploy FAILS.
  //
  // WHAT MAKES THIS ONE NON-OBSTRUCTIVE, concretely:
  //   - bottom-LEFT, not a fixed top bar, so it displaces nothing and covers no
  //     control
  //   - pointer-events-none: it cannot intercept a click even if it overlaps
  //     something. A notice that can swallow a click is an obstruction however
  //     small it looks.
  //   - role="status" + aria-live="polite", not alert/assertive: it should not
  //     interrupt a screen reader mid-sentence for something the user need not
  //     act on.
  //   - no dismiss control, because it disappears on its own within a minute and
  //     a button to close a thing that closes itself is just another thing to
  //     click.
  //
  // Maintenance WINS if both are somehow true: a human taking the system down is
  // the more serious statement, and showing both would be two bars.
  if (state?.deploying && !state.active) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="deploy-indicator"
        className="fixed bottom-4 left-4 z-40 pointer-events-none select-none rounded-full bg-surface-elevated/90 border border-border px-3 py-1.5 shadow-sm backdrop-blur"
      >
        <span className="flex items-center gap-2 text-xs text-text-secondary">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" aria-hidden />
          Updating FTL — you can keep working
        </span>
      </div>
    );
  }

  if (!state || !state.active) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-amber-950 dark:bg-amber-600 dark:text-amber-50 shadow-md border-b border-amber-700"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm font-medium leading-relaxed">
          {state.message}
          {state.started_at && (
            <span className="ml-2 text-xs opacity-80">
              (started {new Date(state.started_at).toLocaleTimeString()})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
